import { db, auth } from '../Firebase/Firebase'
import { collection, addDoc, doc, setDoc, serverTimestamp, increment } from 'firebase/firestore'
import { trackPixel, trackPixelCustom } from './metaPixel'

// Devise du marché (Congo) — requise par les événements e-commerce du Pixel Meta.
const PIXEL_CURRENCY = 'XAF'

/* ── Types d'événements suivis ──────────────────────────────────────────────
 * login          — connexion réussie (OTP validé)
 * logout         — déconnexion explicite
 * product_view   — ouverture d'une fiche produit
 * category_view  — clic sur une catégorie
 * search         — soumission d'une recherche
 * cart_add       — ajout d'un produit au panier
 * cart_remove    — retrait d'un produit du panier
 * checkout_start — lancement de la validation
 * purchase       — commande créée (paiement initié)
 * favorite_add   — ajout aux favoris
 * favorite_remove — retrait des favoris
 * share          — partage d'un produit vers un réseau social / messagerie
 * page_view      — vue de page générique (route)
 */
export type EventType =
  | 'login'
  | 'logout'
  | 'product_view'
  | 'category_view'
  | 'search'
  | 'cart_add'
  | 'cart_remove'
  | 'checkout_start'
  | 'purchase'
  | 'favorite_add'
  | 'favorite_remove'
  | 'share'
  | 'page_view'

interface EventPayload {
  [key: string]: unknown
}

/**
 * Logge un événement client dans Customers/{uid}/Events et met à jour lastSeenAt.
 * Silencieux en cas d'erreur (les events ne doivent jamais bloquer l'UX).
 *
 * @param type     — type d'événement (voir EventType)
 * @param payload  — données associées (productId, query, productNom, etc.)
 * @param uidOverride — UID explicite si l'event a lieu juste avant que
 *                      auth.currentUser ne soit propagé (ex. login).
 */
export async function logEvent(
  type: EventType,
  payload: EventPayload = {},
  uidOverride?: string | null
): Promise<void> {
  const uid = uidOverride ?? auth.currentUser?.uid
  if (!uid) return
  try {
    await addDoc(collection(db, 'Customers', uid, 'Events'), {
      type,
      payload,
      at: serverTimestamp(),
      // Contexte utile pour l'analyse côté admin
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    })
    // Met à jour des compteurs agrégés sur le doc Customer pour le dashboard
    await setDoc(
      doc(db, 'Customers', uid),
      {
        lastSeenAt: serverTimestamp(),
        eventsCount: increment(1),
        [`eventCounts.${type}`]: increment(1),
      },
      { merge: true }
    )
  } catch (err) {
    // tracking ne doit pas faire planter l'app
    if (typeof console !== 'undefined') {
      console.warn('[tracking]', type, err)
    }
  }
}

/* Helpers spécifiques — préfèrent ceux-ci aux appels logEvent() bruts.
 * Chacun déclenche AUSSI l'événement standard Meta Pixel correspondant (pour
 * l'optimisation/retargeting des pubs Meta). Le Pixel est tiré indépendamment du
 * login (un visiteur non connecté compte aussi), donc avant logEvent(). */
export const trackLogin = (phone?: string, uid?: string) =>
  logEvent('login', { phone }, uid)

export const trackLogout = () =>
  logEvent('logout')

export const trackProductView = (productId: string, nom?: string, prix?: number) => {
  trackPixel('ViewContent', {
    content_ids: [productId], content_type: 'product', content_name: nom,
    ...(prix != null ? { value: prix, currency: PIXEL_CURRENCY } : {}),
  })
  return logEvent('product_view', { productId, nom, prix })
}

export const trackCategoryView = (categoryId: string, name?: string) =>
  logEvent('category_view', { categoryId, name })

export const trackSearch = (query: string, resultsCount?: number) => {
  trackPixel('Search', { search_string: query })
  return logEvent('search', { query, resultsCount })
}

export const trackCartAdd = (productId: string, nom?: string, quantite = 1, prix?: number) => {
  trackPixel('AddToCart', {
    content_ids: [productId], content_type: 'product', content_name: nom, num_items: quantite,
    ...(prix != null ? { value: prix * quantite, currency: PIXEL_CURRENCY } : {}),
  })
  return logEvent('cart_add', { productId, nom, quantite, prix })
}

export const trackCartRemove = (productId: string, nom?: string) =>
  logEvent('cart_remove', { productId, nom })

export const trackCheckoutStart = (total: number, itemsCount: number) => {
  trackPixel('InitiateCheckout', { value: total, currency: PIXEL_CURRENCY, num_items: itemsCount })
  return logEvent('checkout_start', { total, itemsCount })
}

export const trackPurchase = (orderId: string, total: number, itemsCount: number) => {
  trackPixel('Purchase', {
    value: total, currency: PIXEL_CURRENCY, num_items: itemsCount,
    content_type: 'product', order_id: orderId,
  })
  return logEvent('purchase', { orderId, total, itemsCount })
}

export const trackFavoriteAdd = (productId: string, nom?: string) => {
  trackPixel('AddToWishlist', { content_ids: [productId], content_type: 'product', content_name: nom })
  return logEvent('favorite_add', { productId, nom })
}

export const trackFavoriteRemove = (productId: string, nom?: string) =>
  logEvent('favorite_remove', { productId, nom })

/** channel : whatsapp | facebook | tiktok | native | copy_link … */
export const trackShare = (productId: string, channel: string, nom?: string) => {
  trackPixelCustom('Share', { content_ids: [productId], content_type: 'product', content_name: nom, channel })
  return logEvent('share', { productId, channel, nom })
}

export const trackPageView = (path: string, title?: string) =>
  logEvent('page_view', { path, title })
